import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { PageHeader, StatusChip, BotAvatar, EmptyState, TableSkeleton, ManagementChip, RuntimeChip, LiveChip } from "@/components/shared";
import { useLiveEvents } from "@/hooks/useLiveEvents";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Search, Bot, PlusCircle, MoreHorizontal, HeartPulse, Radio, ChevronLeft, ChevronRight, Filter } from "lucide-react";

export default function Bots() {
  const navigate = useNavigate();
  const [resp, setResp] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [source, setSource] = useState("all");
  const [management, setManagement] = useState("all");
  const [accounts, setAccounts] = useState([]);
  const [account, setAccount] = useState("all");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState([]);
  // Per-source counts (LOCAL + each connected BotNexus instance) from the same
  // filters the list uses, so the dropdown numbers always match the list total.
  const [facets, setFacets] = useState(null);

  useEffect(() => { api.get("/telegram-accounts").then((r) => setAccounts(r.data.items)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    const params = { page, page_size: 25 };
    if (search) params.search = search;
    if (status !== "all") params.status = status;
    if (management !== "all") params.management = management;
    if (account !== "all") params.account = account;
    // Source is filtered server-side (LOCAL / BOTNEXUS:<backend_id>) so the
    // total and pagination reflect the selection — never a client-side filter of
    // the current page, which showed "No bots found" whenever the hosted bots
    // happened to sit on another page.
    if (source !== "all") params.source = source;
    try {
      const [{ data }, facetRes] = await Promise.all([
        api.get("/bots", { params }),
        api.get("/bots/facets").catch(() => null),
      ]);
      setResp(data);
      if (facetRes) setFacets(facetRes.data);
    } catch (e) { toast.error(apiErr(e)); }
  }, [page, search, status, source, management, account]);

  useEffect(() => { load(); }, [load]);

  // If a filter change or a sync leaves the current page past the end, fall
  // back to page 1 instead of rendering an empty table with a non-zero total.
  useEffect(() => {
    if (resp && resp.items.length === 0 && resp.total > 0 && page > 1) setPage(1);
  }, [resp, page]);

  // A managed bot ingested by the Manager Bot shows up here without a refresh.
  useLiveEvents(["bot.created", "bot.upserted", "bot.provisioned", "bot.updated", "federation.synced", "federation.updated"], () => load());

  const hostedSources = (facets?.sources || []).filter((s) => s.source !== "LOCAL");
  const sourceLabel = (b) => {
    if (b.source === "LOCAL") return b.origin || "local";
    return b.execution_backend?.name || b.execution_backend_name || "BotNexus";
  };

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allChecked = resp?.items?.length > 0 && selected.length === resp.items.length;

  const runBulk = async (action) => {
    if (selected.length === 0) return;
    try {
      const { data } = await api.post("/bulk", { bot_ids: selected, action });
      toast.success(`Bulk ${action} started`, { description: "Track progress in Jobs." });
      setSelected([]);
      navigate("/jobs");
    } catch (e) { toast.error(apiErr(e)); }
  };

  // Lifecycle action on a BotNexus-hosted bot, proxied through the job queue.
  const hostedAction = async (b, action) => {
    try {
      await api.post(`/federation/bots/${b.id}/action`, { action });
      toast.success(`${action.charAt(0).toUpperCase() + action.slice(1)} requested`,
        { description: `${b.name} · running on BotNexus` });
      setTimeout(load, 2500);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-5">
      <PageHeader title="All Bots" desc="Search, filter and operate on every bot at once."
        actions={<div className="flex items-center gap-2"><LiveChip /><Button size="sm" onClick={() => navigate("/bots/create")} className="gap-1.5" data-testid="btn-new-bot"><PlusCircle size={15} /> New Bot</Button></div>} testId="bots-header" />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="input-search-bots" className="pl-9" placeholder="Search by name, @username or bot ID…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger className="w-[150px]" data-testid="select-filter-status"><Filter size={13} className="mr-1" /><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="pending_confirmation">Pending</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <Select value={source} onValueChange={(v) => { setSource(v); setPage(1); }}>
          <SelectTrigger className="w-[190px]" data-testid="select-filter-source"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources{facets ? ` (${facets.total})` : ""}</SelectItem>
            <SelectItem value="LOCAL" data-testid="source-option-local">Local · BotControl{facets ? ` (${facets.local})` : ""}</SelectItem>
            {facets ? hostedSources.map((s) => (
              <SelectItem key={s.source} value={s.source} data-testid={`source-option-${s.backend_id || "hosted"}`}>
                {s.label} ({s.count})
              </SelectItem>
            )) : <SelectItem value="BOTNEXUS">BotNexus (hosted)</SelectItem>}
            {facets && hostedSources.length > 1 && <SelectItem value="BOTNEXUS">Any BotNexus ({facets.hosted})</SelectItem>}
          </SelectContent>
        </Select>
        <Select value={management} onValueChange={(v) => { setManagement(v); setPage(1); }}>
          <SelectTrigger className="w-[160px]" data-testid="select-filter-management"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All management</SelectItem>
            <SelectItem value="FULL_MANAGED">Managed</SelectItem>
            <SelectItem value="TOKEN_CONNECTED">Token connected</SelectItem>
            <SelectItem value="OWNER_CONTROL">Owner only</SelectItem>
            <SelectItem value="ACTION_REQUIRED">Action required</SelectItem>
          </SelectContent>
        </Select>
        {accounts.length > 0 && (
          <Select value={account} onValueChange={(v) => { setAccount(v); setPage(1); }}>
            <SelectTrigger className="w-[170px]" data-testid="select-filter-account"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All accounts</SelectItem>
              {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.first_name || a.username || a.phone_masked}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {selected.length > 0 && (
        <div className="flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-lg px-4 py-2.5 animate-fade-in" data-testid="bulk-bar">
          <span className="text-sm font-medium">{selected.length} selected</span>
          <div className="flex-1" />
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => runBulk("health_check")} data-testid="btn-bulk-health"><HeartPulse size={14} /> Health check</Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected([])}>Clear</Button>
        </div>
      )}

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {!resp ? <TableSkeleton /> : resp.total === 0 ? (
          <EmptyState icon={Bot} title="No bots found"
            desc={source !== "all" && source !== "LOCAL" ? "This BotNexus instance has no synced bots yet. Run a sync from Settings → Execution backends." : "Try adjusting filters, or create a new bot."}
            action={<Button size="sm" onClick={() => navigate("/bots/create")}>Create a bot</Button>} testId="empty-bots" />
        ) : resp.items.length === 0 ? <TableSkeleton /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-secondary/50 border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 w-8"><Checkbox checked={allChecked} onCheckedChange={(c) => setSelected(c ? resp.items.map((b) => b.id) : [])} data-testid="checkbox-select-all" /></th>
                  <th className="px-4 py-3 text-left font-semibold">Bot</th>
                  <th className="px-4 py-3 text-left font-semibold">Owner Account</th>
                  <th className="px-4 py-3 text-left font-semibold">Management</th>
                  <th className="px-4 py-3 text-left font-semibold">Status</th>
                  <th className="px-4 py-3 text-left font-semibold">Source</th>
                  <th className="px-4 py-3 text-left font-semibold">Health</th>
                  <th className="px-4 py-3 text-left font-semibold">Last Activity</th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {resp.items.map((b) => (
                  <tr key={b.id} data-testid={`bot-row-${b.id}`} className="border-b border-border/60 hover:bg-secondary/40 transition-colors cursor-pointer" onClick={() => navigate(`/bots/${b.id}`)}>
                    <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}><Checkbox checked={selected.includes(b.id)} onCheckedChange={() => toggle(b.id)} data-testid={`checkbox-bot-${b.id}`} /></td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <BotAvatar name={b.name} username={b.username} size={30} />
                        <div className="min-w-0">
                          <div className="font-medium truncate flex items-center gap-1.5">{b.name}<RuntimeChip runtime={b.runtime} backend={b.execution_backend_name} /></div>
                          <div className="text-xs text-muted-foreground mono truncate">{b.runtime === "botnexus" ? (b.external?.adapter || b.type || "hosted") : ("@" + (b.username || "—"))}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">{b.owner_account_name || "—"}</td>
                    <td className="px-4 py-2.5"><ManagementChip status={b.management_status || "OWNER_CONTROL"} /></td>
                    <td className="px-4 py-2.5"><StatusChip status={b.status} /></td>
                    <td className="px-4 py-2.5"><span className="text-xs capitalize px-2 py-0.5 rounded bg-secondary" title={b.source} data-testid={`bot-source-${b.id}`}>{sourceLabel(b)}</span></td>
                    <td className="px-4 py-2.5"><StatusChip status={b.health?.status || "OFFLINE"} pulse /></td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">{b.last_activity_at ? new Date(b.last_activity_at).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`btn-bot-actions-${b.id}`}><MoreHorizontal size={15} /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/bots/${b.id}`)}>Manage</DropdownMenuItem>
                          {b.runtime === "botnexus" && b.external?.state !== "running" && <DropdownMenuItem onClick={() => hostedAction(b, "start")} data-testid={`btn-start-${b.id}`}>Start</DropdownMenuItem>}
                          {b.runtime === "botnexus" && <DropdownMenuItem onClick={() => hostedAction(b, "restart")} data-testid={`btn-restart-${b.id}`}>Restart</DropdownMenuItem>}
                          {b.runtime === "botnexus" && b.external?.state === "running" && <DropdownMenuItem className="text-rose-500" onClick={() => hostedAction(b, "stop")} data-testid={`btn-stop-${b.id}`}>Stop</DropdownMenuItem>}
                          {b.username && <DropdownMenuItem onClick={() => window.open(`https://t.me/${b.username}`, "_blank")}>Open in Telegram</DropdownMenuItem>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {resp && resp.total > 25 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground" data-testid="bots-total">{resp.total} bots</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" className="h-8 w-8" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}><ChevronLeft size={15} /></Button>
            <span className="mono text-xs">Page {page} / {Math.ceil(resp.total / 25)}</span>
            <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= Math.ceil(resp.total / 25)} onClick={() => setPage((p) => p + 1)}><ChevronRight size={15} /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
